import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, Code, Key, Zap, Shield, BookOpen, Copy, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const ApiDocs = () => {
  const navigate = useNavigate();
  const [activeEndpoint, setActiveEndpoint] = useState('upload');
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Use environment variable for API base URL
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const API_BASE_URL = `${BACKEND_URL}/api`;

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(type);
    toast.success('Code copied to clipboard!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const endpoints = {
    upload: {
      title: "Upload & Process PDF",
      method: "POST",
      endpoint: "/api/process-pdf",
      description: "Upload a PDF bank statement for AI-powered analysis and categorization",
      request: {
        contentType: "multipart/form-data",
        body: {
          file: "PDF file (max 50MB)",
          userId: "string (required)",
          options: {
            includeScheduleC: "boolean (default: true)",
            customCategories: "array of strings (optional)"
          }
        }
      },
      response: {
        processId: "string",
        status: "processing",
        estimatedTime: "number (seconds)",
        cost: {
          uploadFee: 1.00,
          pageFee: "number (pages * $1.00)",
          totalCost: "number"
        }
      },
      codeExample: `curl -X POST ${BACKEND_URL}/api/process-pdf \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: multipart/form-data" \
  -F "file=@/path/to/statement.pdf" \
  -F "userId=user123" \
  -F "options={\"includeScheduleC\":true}"`
    },
    status: {
      title: "Check Processing Status",
      method: "GET",
      endpoint: "/api/process-status/{processId}",
      description: "Check the status of a PDF processing job",
      request: {
        parameters: {
          processId: "string (path parameter)"
        }
      },
      response: {
        processId: "string",
        status: "processing | completed | failed",
        progress: "number (0-100)",
        result: {
          totalTransactions: "number",
          categorizedTransactions: "number",
          potentialDeductions: "number",
          scheduleC: "object (if requested)"
        }
      },
      codeExample: `curl -X GET ${BACKEND_URL}/api/process-status/abc123 \
  -H "Authorization: Bearer YOUR_API_KEY"`
    },
    results: {
      title: "Download Results",
      method: "GET",
      endpoint: "/api/results/{processId}",
      description: "Download the processed results in various formats",
      request: {
        parameters: {
          processId: "string (path parameter)",
          format: "json | excel | csv (query parameter)"
        }
      },
      response: {
        format: "Depends on requested format",
        data: {
          transactions: "array of categorized transactions",
          summary: "financial summary object",
          scheduleC: "Schedule C formatted data",
          metadata: "processing information"
        }
      },
      codeExample: `curl -X GET ${BACKEND_URL}/api/results/abc123?format=json \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -o results.json`
    },
    account: {
      title: "Account Information",
      method: "GET",
      endpoint: "/api/account",
      description: "Get account information, usage statistics, and billing details",
      request: {},
      response: {
        userId: "string",
        email: "string",
        accountType: "individual | business",
        usage: {
          totalProcessed: "number",
          currentMonth: "number",
          remainingCredits: "number"
        },
        billing: {
          currentBalance: "number",
          lastPayment: "date string",
          nextBilling: "date string"
        }
      },
      codeExample: `curl -X GET ${BACKEND_URL}/api/account \
  -H "Authorization: Bearer YOUR_API_KEY"`
    }
  };

  const currentEndpoint = endpoints[activeEndpoint];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Code className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              API Documentation
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Integrate Tax Helper Pros's AI-powered document processing into your applications
          </p>
        </div>

        {/* API Overview */}
        <Card className="shadow-xl border-0 mb-8">
          <CardHeader className="bg-blue-50 border-b">
            <CardTitle className="flex items-center gap-3 text-blue-800">
              <Zap className="w-6 h-6" />
              API Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-8 h-8 text-green-600" />
                </div>
                <h4 className="font-semibold mb-2 text-slate-800">RESTful API</h4>
                <p className="text-sm text-slate-600">Simple HTTP requests with JSON responses</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="font-semibold mb-2 text-slate-800">Secure Authentication</h4>
                <p className="text-sm text-slate-600">API key-based authentication with rate limiting</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <BookOpen className="w-8 h-8 text-purple-600" />
                </div>
                <h4 className="font-semibold mb-2 text-slate-800">Comprehensive Docs</h4>
                <p className="text-sm text-slate-600">Detailed examples and code snippets</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Authentication */}
        <Card className="shadow-lg border-0 mb-8">
          <CardHeader className="bg-green-50 border-b">
            <CardTitle className="flex items-center gap-3 text-green-800">
              <Key className="w-6 h-6" />
              Authentication
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Base URL</h4>
                <div className="bg-slate-100 p-3 rounded-lg font-mono text-sm">
                  {BACKEND_URL}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Authentication Header</h4>
                <div className="bg-slate-100 p-3 rounded-lg font-mono text-sm">
                  Authorization: Bearer YOUR_API_KEY
                </div>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-orange-800 text-sm">
                  <strong>Getting API Keys:</strong> API access is currently in beta. Contact us at api@taxhelperpros.com 
                  to request access and receive your API key.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Endpoint Navigation */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg border-0 sticky top-24">
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle className="text-slate-800">Endpoints</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1">
                  {Object.entries(endpoints).map(([key, endpoint]) => (
                    <button
                      key={key}
                      onClick={() => setActiveEndpoint(key)}
                      className={`w-full text-left p-4 transition-colors ${
                        activeEndpoint === key
                          ? 'bg-blue-100 text-blue-800 border-r-2 border-blue-600'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <div className="font-medium">{endpoint.title}</div>
                      <div className="text-xs opacity-75">{endpoint.method} {endpoint.endpoint}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Endpoint Details */}
          <div className="lg:col-span-3">
            <Card className="shadow-lg border-0">
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle className="flex items-center justify-between">
                  <div>
                    <div className="text-slate-800">{currentEndpoint.title}</div>
                    <div className="text-sm font-normal text-slate-600 mt-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        currentEndpoint.method === 'GET' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {currentEndpoint.method}
                      </span>
                      <span className="ml-2 font-mono text-sm">{currentEndpoint.endpoint}</span>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-2">Description</h4>
                    <p className="text-slate-700">{currentEndpoint.description}</p>
                  </div>

                  {currentEndpoint.request && Object.keys(currentEndpoint.request).length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-3">Request</h4>
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <pre className="text-sm text-slate-700 whitespace-pre-wrap">
                          {JSON.stringify(currentEndpoint.request, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3">Response</h4>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <pre className="text-sm text-slate-700 whitespace-pre-wrap">
                        {JSON.stringify(currentEndpoint.response, null, 2)}
                      </pre>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-800">Example Request</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(currentEndpoint.codeExample, activeEndpoint)}
                        className="text-xs"
                      >
                        {copiedCode === activeEndpoint ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Copied</>
                        ) : (
                          <><Copy className="w-3 h-3 mr-1" /> Copy</>
                        )}
                      </Button>
                    </div>
                    <div className="bg-slate-900 text-green-400 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm whitespace-pre-wrap">
                        {currentEndpoint.codeExample}
                      </pre>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Rate Limits & Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          <Card className="shadow-lg border-0">
            <CardHeader className="bg-yellow-50 border-b">
              <CardTitle className="text-yellow-800">Rate Limits</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-700">Requests per minute</span>
                  <span className="font-medium text-slate-800">60</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-700">Requests per day</span>
                  <span className="font-medium text-slate-800">1,000</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-700">Max file size</span>
                  <span className="font-medium text-slate-800">50 MB</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="bg-green-50 border-b">
              <CardTitle className="text-green-800">API Pricing</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-700">PDF Upload</span>
                  <span className="font-medium text-slate-800">$1.00</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-700">Per Page Processing</span>
                  <span className="font-medium text-slate-800">$1.00</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-700">API Requests</span>
                  <span className="font-medium text-slate-800">Free</span>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                  <p className="text-blue-800 text-sm">
                    Same transparent pricing as our web app - no additional API fees!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Support */}
        <Card className="shadow-xl border-2 border-purple-200 bg-purple-50 mt-12">
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Code className="w-8 h-8 text-purple-600" />
              <h3 className="text-2xl font-bold text-purple-800">Need API Support?</h3>
            </div>
            <p className="text-purple-700 text-lg mb-6">
              Our API is currently in beta. We're working with select partners to refine the experience 
              before public launch.
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                onClick={() => window.open('mailto:api@taxhelperpros.com')}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Request API Access
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/contact')}
                className="border-purple-600 text-purple-600 hover:bg-purple-50"
              >
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/auth')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Try Web App
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApiDocs;